// Modal con detalle de un abogado (apoderado): stats agregadas, top
// expedientes, distribución por módulo, info de honorarios.
// Se invoca desde el dashboard ejecutivo (Tab Operativo + búsqueda en header).

import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Grid, Paper, Skeleton, Alert, Chip, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    ToggleButton, ToggleButtonGroup, TextField,
} from '@mui/material';
import { dashEjecAbogadoDetalleJWT } from '../../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;

const fmtNum = (n) => Number(n || 0).toLocaleString('es-AR');
const fmtMoney = (n) => Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
const fmtFecha = (s) => s ? new Date(s).toLocaleDateString('es-AR') : '—';

const fmtIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const PERIODOS = {
    mes:        () => { const h = new Date(); const d = new Date(h.getFullYear(), h.getMonth(), 1); return { desde: fmtIso(d), hasta: fmtIso(h) }; },
    trimestre:  () => { const h = new Date(); const d = new Date(h.getFullYear(), h.getMonth() - 2, 1); return { desde: fmtIso(d), hasta: fmtIso(h) }; },
    ano:        () => { const h = new Date(); const d = new Date(h.getFullYear(), 0, 1); return { desde: fmtIso(d), hasta: fmtIso(h) }; },
    anoAnt:     () => { const y = new Date().getFullYear() - 1; return { desde: `${y}-01-01`, hasta: `${y}-12-31` }; },
};
const PRESET_LABEL = { mes: 'Mes actual', trimestre: 'Últimos 3 meses', ano: 'Año actual', anoAnt: 'Año anterior', custom: 'Período seleccionado' };

export default function AbogadoDetalleDialog({ open, idPersonal, onClose, initialPreset, initialDesde, initialHasta }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Filtro de período local al dialog. Por default arranca con el período que
    // viene del header del Dashboard (para que el detalle coincida con el rango
    // visible en Asignación / Resumen). Si no se pasa, cae a "mes actual".
    const [periodoPreset, setPeriodoPreset] = useState(initialPreset || 'mes');
    const [periodoDesde, setPeriodoDesde] = useState(initialDesde ?? (() => PERIODOS.mes().desde));
    const [periodoHasta, setPeriodoHasta] = useState(initialHasta ?? (() => PERIODOS.mes().hasta));

    const setPreset = (preset) => {
        setPeriodoPreset(preset);
        if (preset !== 'custom') {
            const { desde, hasta } = PERIODOS[preset]();
            setPeriodoDesde(desde);
            setPeriodoHasta(hasta);
        }
    };

    // Reset al período del header cuando se abre o cambia el abogado (no entre
    // re-renders del mismo abogado en la misma sesión).
    useEffect(() => {
        if (!open) return;
        setPeriodoPreset(initialPreset || 'mes');
        if (initialDesde) setPeriodoDesde(initialDesde);
        if (initialHasta) setPeriodoHasta(initialHasta);
        // eslint-disable-next-line
    }, [open, idPersonal]);

    useEffect(() => {
        if (!open || !idPersonal) return;
        let cancel = false;
        setLoading(true);
        setError('');
        setData(null);
        dashEjecAbogadoDetalleJWT(idPersonal, periodoDesde, periodoHasta)
            .then(r => {
                if (cancel) return;
                if (r?.ok) setData(r.data);
                else setError(r?.mensaje || 'Error al cargar detalle');
            })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [open, idPersonal, periodoDesde, periodoHasta]);

    const a = data?.abogado || {};
    const s = data?.stats || {};
    const h = data?.honorarios || {};
    const ar = data?.actividadReciente || {};

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{ color: PRIMARY, fontWeight: 700 }}>
                {loading ? 'Cargando...' : (a.nombre || 'Abogado')}
                {!loading && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#777', fontWeight: 400 }}>
                        {a.Funcion || '—'} {a.Area ? `· ${a.Area}` : ''}{a.matricula ? ` · Matrícula ${a.matricula}` : ''}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            {[1, 2, 3, 4].map(i => (<Grid key={i} size={{ xs: 6, sm: 3 }}>
                                <Skeleton variant="rounded" height={70} sx={{ borderRadius: `${BR}px` }} />
                            </Grid>))}
                        </Grid>
                        <Skeleton variant="rounded" height={300} sx={{ borderRadius: `${BR}px` }} />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : !data ? null : (
                    <>
                        {/* Filtro de período del abogado */}
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: `${BR}px`, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase' }}>
                                Período:
                            </Typography>
                            <ToggleButtonGroup size="small" exclusive value={periodoPreset}
                                onChange={(_, v) => v && setPreset(v)}>
                                <ToggleButton value="mes" sx={{ textTransform: 'none' }}>Mes actual</ToggleButton>
                                <ToggleButton value="trimestre" sx={{ textTransform: 'none' }}>Trim.</ToggleButton>
                                <ToggleButton value="ano" sx={{ textTransform: 'none' }}>Año actual</ToggleButton>
                                <ToggleButton value="anoAnt" sx={{ textTransform: 'none' }}>Año ant.</ToggleButton>
                                <ToggleButton value="custom" sx={{ textTransform: 'none' }}>Custom</ToggleButton>
                            </ToggleButtonGroup>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField type="date" size="small" label="Desde" value={periodoDesde}
                                    onChange={(e) => { setPeriodoDesde(e.target.value); setPeriodoPreset('custom'); }}
                                    InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                                <TextField type="date" size="small" label="Hasta" value={periodoHasta}
                                    onChange={(e) => { setPeriodoHasta(e.target.value); setPeriodoPreset('custom'); }}
                                    InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                            </Stack>
                        </Paper>

                        {/* Stats */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Paper sx={{ p: 1.5, borderRadius: `${BR}px`, bgcolor: '#f5f5f5' }}>
                                    <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase' }}>Total exp.</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>{fmtNum(s.total)}</Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Paper sx={{ p: 1.5, borderRadius: `${BR}px`, bgcolor: '#e3f2fd' }}>
                                    <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase' }}>Judiciales en trámite</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1565c0' }}>{fmtNum(s.judicialesVivos)}</Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Paper sx={{ p: 1.5, borderRadius: `${BR}px`, bgcolor: '#fff3e0' }}>
                                    <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase' }}>Administrativos</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#e65100' }}>{fmtNum(s.administrativos)}</Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Paper sx={{ p: 1.5, borderRadius: `${BR}px`, bgcolor: '#ffebee' }}>
                                    <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase' }}>Monto demandado</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#c62828' }}>{fmtMoney(s.montoDemandado)}</Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Honorarios + actividad */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 12, md: 8 }}>
                                <Paper sx={{ p: 2, borderRadius: `${BR}px` }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#2e7d32' }}>Honorarios</Typography>
                                    <Grid container spacing={1.5}>
                                        <Grid size={{ xs: 4 }}>
                                            <Typography variant="caption" color="text.secondary">Devengado</Typography>
                                            <Typography sx={{ fontWeight: 700 }}>{fmtMoney(h.devengado)}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 4 }}>
                                            <Typography variant="caption" color="text.secondary">Depositado</Typography>
                                            <Typography sx={{ fontWeight: 700, color: '#2e7d32' }}>{fmtMoney(h.depositado)}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 4 }}>
                                            <Typography variant="caption" color="text.secondary">Saldo</Typography>
                                            <Typography sx={{ fontWeight: 700, color: '#c62828' }}>{fmtMoney(h.saldo)}</Typography>
                                        </Grid>
                                    </Grid>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#888' }}>
                                        {fmtNum(h.cantPlanes)} plan(es) de pago asociado(s)
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>Actividad — {PRESET_LABEL[periodoPreset] || 'Período'}</Typography>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Iniciados</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>{fmtNum(s.iniciadosPeriodo)}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Movimientos</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>{fmtNum(ar.movsPeriodo)}</Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Distribución por módulo */}
                        {(data.porModulo || []).length > 0 && (
                            <Paper sx={{ p: 2, borderRadius: `${BR}px`, mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>Distribución por módulo (top 10)</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Módulo</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Expedientes</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.porModulo.map((m, i) => (
                                                <TableRow key={i} hover>
                                                    <TableCell sx={{ fontSize: '0.82rem' }}>{m.modulo}</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 700, color: PRIMARY }}>{fmtNum(m.cant)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}

                        {/* Lista de expedientes */}
                        <Paper sx={{ p: 2, borderRadius: `${BR}px` }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                                Expedientes iniciados — {PRESET_LABEL[periodoPreset] || 'Período'} (top 50)
                            </Typography>
                            <TableContainer sx={{ maxHeight: 380 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Autos / Exp.</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Carátula</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Inicio</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Monto</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(data.expedientes || []).map(e => {
                                            const tipo = Number(e.tipo) === 1 ? 'judicial' : 'administrativo';
                                            return (
                                                <TableRow key={e.Id} hover sx={{ cursor: 'pointer' }}
                                                    onClick={() => window.open(`/expediente/${tipo}/${e.Id}`, '_blank')}>
                                                    <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{e.autos || `#${e.Id}`}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{e.Caratula}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.78rem' }}>
                                                        <Chip size="small"
                                                            label={tipo === 'judicial' ? 'Jud.' : 'Adm.'}
                                                            color={tipo === 'judicial' ? 'primary' : 'warning'}
                                                            sx={{ height: 18 }} />
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.78rem', color: '#555' }}>{e.estado || '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.78rem' }}>{fmtFecha(e.Fecha_Inicio)}</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#c62828' }}>{fmtMoney(e.monto)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {(data.expedientes || []).length === 0 && (
                                            <TableRow><TableCell colSpan={6} sx={{ color: '#888', fontStyle: 'italic', textAlign: 'center' }}>
                                                Sin expedientes asignados
                                            </TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
}
